/**
 * The shareholder report: officer wages against distributions, and stock
 * basis. Handoff v14 Tier 3 item 9, on the operator's answer to issue #16
 * (2026-10-08): "Option 2 with a stock basis calculation also".
 *
 * THE FIGURES ARE THE SERVER'S. GET /reports/shareholder adds the quarters up
 * and runs Form 7203 from the books' first year
 * (apps/api/src/services/shareholder.ts), so this screen is not a second
 * place that does the arithmetic.
 *
 * ONE WARNING ABOUT COMPENSATION, by the operator's choice of option 2:
 * distributions taken before the year's first officer wage. The ratio is
 * shown, never judged -- there is no bright line, and a threshold the
 * operator learns to ignore is worse than none. The second warning is basis
 * running out, where the rule is not a judgement: a distribution over basis
 * is generally a capital gain.
 *
 * WHAT IS NOT COUNTED IS SAID: a draft with lines on these accounts (a pay
 * run waiting for its bank debit) is named above everything else, because a
 * wage total short by a whole quarter makes the distributions look larger
 * than they are. The assumptions behind the basis figure are in an InfoNote,
 * reachable from a glance and not repeated on every visit.
 *
 * Its own address beside Cashflow, not a fourth tab of the Reports page:
 * the three statements share a From/To period, and this report has a tax
 * year instead.
 */

import { useEffect, useState } from 'react';
import { PrintButton, PrintHeading } from '../components/PrintReport.js';
import { Link, useSearchParams } from 'react-router-dom';
import { InfoNote } from '../components/InfoNote.js';
import { ApiError, api, type BasisYear, type ShareholderReport } from '../api.js';
import { formatCents } from '../lib/money.js';

export function ShareholderPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const yearParam = searchParams.get('year');
  const year = yearParam === null ? undefined : Number(yearParam);
  const [report, setReport] = useState<ShareholderReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .getShareholder(year)
      .then((result) => {
        if (!live) return;
        setReport(result.report);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (live) setError(caught instanceof ApiError ? caught.message : 'Could not read the shareholder report.');
      });
    return () => {
      live = false;
    };
  }, [year]);

  function choose(value: string) {
    const params = new URLSearchParams(searchParams);
    params.set('year', value);
    setSearchParams(params, { replace: true });
  }

  // The year in the address, even while it loads or after it failed, so the
  // choice on screen is never one year while the page reads another.
  const shown = year ?? report?.year;
  const choices =
    report === null ? [] : [...new Set([...report.years, ...(shown === undefined ? [] : [shown])])].sort((a, b) => b - a);

  return (
    <section className="reports shareholder">
      {/* The year is chosen in a control, which does not print, so it is said here. */}
      <PrintHeading covers={shown === undefined ? undefined : `Tax year ${shown}`} />
      <header className="row reports-head">
        <h1>Shareholder</h1>
        <span className="muted">
          Officer wages, distributions and stock basis for one tax year, from posted entries. A prompt for your CPA,
          never a ruling.
        </span>
        <PrintButton />
      </header>

      <nav className="row tabs" aria-label="Report">
        <Link to="/reports?report=income" className="tab">
          Income Statement
        </Link>
        <Link to="/reports?report=balance" className="tab">
          Balance Sheet
        </Link>
        <Link to="/reports?report=trial" className="tab">
          Trial Balance
        </Link>
        <Link to="/reports/cashflow" className="tab">
          Cashflow
        </Link>
        <span className="tab is-current" aria-current="page">
          Shareholder
        </span>
      </nav>

      {report !== null && shown !== undefined && (
        <div className="row report-period">
          <label>
            Year{' '}
            <select value={shown} onChange={(event) => choose(event.target.value)}>
              {choices.map((choice) => (
                <option key={choice} value={choice}>
                  {choice}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {error === null && (report === null || report.year !== shown) && <p className="muted">Loading…</p>}
      {error === null && report !== null && report.year === shown && <Year report={report} />}
    </section>
  );
}

function Year({ report }: { report: ShareholderReport }) {
  const { totals, notPosted } = report;
  const together = totals.wagesCents + totals.distributionsCents;
  const one = notPosted.entries === 1;
  const leftOut = [
    notPosted.wagesCents !== 0 && `${formatCents(notPosted.wagesCents)} of officer wages`,
    notPosted.distributionsCents !== 0 && `${formatCents(notPosted.distributionsCents)} of distributions`,
    notPosted.contributionsCents !== 0 && `${formatCents(notPosted.contributionsCents)} of contributions`,
  ].filter(Boolean);

  return (
    <>
      {notPosted.entries > 0 && (
        <p className="notice notice-warn" role="status">
          {one ? `One draft entry in ${report.year} touches` : `${notPosted.entries} draft entries in ${report.year} touch`}{' '}
          these accounts and {one ? 'is' : 'are'} not counted{leftOut.length > 0 ? `: ${leftOut.join(', ')}` : ''}. Post{' '}
          {one ? 'it' : 'them'} to count {one ? 'it' : 'them'} here.
        </p>
      )}
      {report.uncategorized > 0 && (
        <p className="notice notice-warn" role="status">
          {report.uncategorized === 1
            ? `One bank transaction in ${report.year} is uncategorized, so it is`
            : `${report.uncategorized} bank transactions in ${report.year} are uncategorized, so they are`}{' '}
          in no figure here. A distribution among them is not counted until it is categorized and posted.
        </p>
      )}

      <h2>
        Officer Wages And Distributions{' '}
        <InfoNote label="How This Is Read">
          Posted entries on {report.accounts.wages.map((account) => `${account.code} ${account.name}`).join(', ') || 'the officer compensation account'}
          , the owner&apos;s health insurance and {report.accounts.distributions.code}{' '}
          {report.accounts.distributions.name}, by quarter of their entry date: a pay run&apos;s is its pay date, and a
          bank transaction&apos;s is the day the money moved. There is no fixed ratio for reasonable compensation, so
          the figures are shown and not judged. The one warning is a distribution taken before the year&apos;s first
          officer wage, which is the pattern the IRS questions.
        </InfoNote>
      </h2>

      {report.distributionsBeforeWages && (
        <p className="notice notice-warn" role="status">
          {formatCents(report.distributionsBeforeWages.cents)} of distributions{' '}
          {report.distributionsBeforeWages.entries === 1 ? 'was' : 'were'} taken{' '}
          {report.firstWageDate === null
            ? `in ${report.year} with no officer wage paid that year`
            : `before the first officer wage of ${report.year}, paid ${report.firstWageDate}`}
          . Ask your CPA whether the salary is reasonable compensation for the work.
        </p>
      )}

      <div className="table-scroll">
        <table className="lines" aria-label="Officer Wages And Distributions">
          <thead>
            <tr>
              <th>Quarter</th>
              <th className="num">Officer Wages</th>
              <th className="num">Officer Health Insurance</th>
              <th className="num">Distributions</th>
            </tr>
          </thead>
          <tbody>
            {report.quarters.map((quarter) => (
              <tr key={quarter.quarter}>
                <td>Q{quarter.quarter}</td>
                <td className="num">{formatCents(quarter.wagesCents)}</td>
                <td className="num">{formatCents(quarter.healthInsuranceCents)}</td>
                <td className="num">{formatCents(quarter.distributionsCents)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>
                <strong>Total</strong>
              </td>
              <td className="num">
                <strong>{formatCents(totals.wagesCents)}</strong>
              </td>
              <td className="num">
                <strong>{formatCents(totals.healthInsuranceCents)}</strong>
              </td>
              <td className="num">
                <strong>{formatCents(totals.distributionsCents)}</strong>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      {together > 0 && (
        <p className="muted">
          Officer wages are {Math.round((totals.wagesCents / together) * 100)}% and distributions{' '}
          {Math.round((totals.distributionsCents / together) * 100)}% of the two together.
        </p>
      )}

      <Basis report={report} />
    </>
  );
}

function Basis({ report }: { report: ShareholderReport }) {
  const basis = report.basis;
  const earlier = report.basisHistory.slice(0, -1);
  const heading = (
    <h2>
        Stock Basis{' '}
        <InfoNote label="What This Assumes">
          Form 7203&apos;s order, which the regulations fix: contributions and income first, then distributions, then
          losses and deductions. Income is the cash-basis result, as the return is filed. Charitable contributions
          ({report.accounts.charitable ? `${report.accounts.charitable.code} ${report.accounts.charitable.name}` : 'none marked'})
          come off after distributions, because the return states them separately. Nondeductible expenses such as the
          disallowed half of meals are not separated: they stay inside income, which can only bring a warning earlier.
          The books&apos; first year, {report.firstYear}, starts at zero basis, which holds when the books begin when the
          company did. Debt basis, from a loan you made to the company, is not covered. The Form 7203 your CPA files is
          the authority; if its figures differ, theirs are right. It is computed for one shareholder: every
          distribution and all of the income are taken as theirs.
        </InfoNote>
      </h2>
  );

  // A year since the books began that the cash basis cannot convert withholds
  // the basis, in words, and nothing above it (review SH-1).
  if (basis === null) {
    const refused = report.basisUnavailable;
    return (
      <>
        {heading}
        <p className="notice notice-warn" role="status">
          Stock basis runs from {report.firstYear}, and {refused?.year ?? 'an earlier year'} cannot be put on the cash
          basis yet: {refused?.message ?? 'the server did not say why.'}
        </p>
      </>
    );
  }

  return (
    <>
      {heading}

      {basis.distributionsOverBasisCents > 0 && (
        <p className="notice notice-warn" role="status">
          {formatCents(basis.distributionsOverBasisCents)} of {report.year}&apos;s distributions exceeded stock basis.
          A distribution over basis is generally taxed as a capital gain. Ask your CPA.
        </p>
      )}

      <div className="table-scroll">
        <table className="lines" aria-label="Stock Basis">
          <thead>
            <tr>
              <th>Line</th>
              <th>{report.year}</th>
              <th className="num">Amount</th>
            </tr>
          </thead>
          <tbody>
            <BasisRow line="1" label="Basis At The Start Of The Year" cents={basis.beginningCents} />
            <BasisRow line="2" label="Capital Contributions" cents={basis.contributionsCents} />
            <BasisRow line="3a" label="Ordinary Business Income" cents={basis.ordinaryIncomeCents} />
            <BasisRow line="5" label="Basis Before Distributions" cents={basis.beforeDistributionsCents} strong />
            <BasisRow line="6" label="Distributions" cents={basis.distributionsCents} />
            <BasisRow line="7" label="Basis After Distributions" cents={basis.afterDistributionsCents} strong />
            <BasisRow line="9" label="Nondeductible Expenses" cents={basis.nondeductibleCents} note="Not separated" />
            <BasisRow line="10" label="Basis Before Losses And Deductions" cents={basis.beforeLossesCents} strong />
            <BasisRow line="11" label="Losses And Deductions Allowed" cents={basis.allowedLossesCents} />
            {basis.ordinaryLossCents > 0 && <BasisRow label="Of which ordinary loss" cents={basis.ordinaryLossCents} part />}
            {basis.charitableCents > 0 && (
              <BasisRow label="Of which charitable contributions" cents={basis.charitableCents} part />
            )}
            {basis.carriedInLossesCents > 0 && (
              <BasisRow label="Of which carried from earlier years" cents={basis.carriedInLossesCents} part />
            )}
            <BasisRow line="15" label="Basis At The End Of The Year" cents={basis.endingCents} strong />
            {basis.suspendedLossesCents > 0 && (
              <BasisRow label="Losses Carried To Next Year" cents={basis.suspendedLossesCents} />
            )}
          </tbody>
        </table>
      </div>
      {(basis.ordinaryLossCents > 0 || basis.charitableCents > 0 || basis.carriedInLossesCents > 0) &&
        basis.suspendedLossesCents > 0 && (
          <p className="muted">
            Line 11 allows the losses and deductions only up to the basis left after distributions; the rest waits for a
            later year&apos;s basis.
          </p>
        )}

      {earlier.length > 0 && <EarlierYears years={earlier} />}
    </>
  );
}

function BasisRow({
  line,
  label,
  cents,
  strong,
  part,
  note,
}: {
  line?: string;
  label: string;
  cents: number;
  strong?: boolean;
  part?: boolean;
  note?: string;
}) {
  const amount = formatCents(cents);
  return (
    <tr>
      <td className="muted">{line ?? ''}</td>
      <td className={part ? 'muted' : undefined}>
        {strong ? <strong>{label}</strong> : label}
        {note && <span className="muted"> ({note})</span>}
      </td>
      <td className={`num${part ? ' muted' : ''}`}>{strong ? <strong>{amount}</strong> : amount}</td>
    </tr>
  );
}

function EarlierYears({ years }: { years: BasisYear[] }) {
  return (
    <>
      <h3>Earlier Years</h3>
      <div className="table-scroll">
        <table className="lines" aria-label="Earlier Years">
          <thead>
            <tr>
              <th>Year</th>
              <th className="num">Basis At The Start</th>
              <th className="num">Distributions</th>
              <th className="num">Basis At The End</th>
              <th className="num">Losses Carried Forward</th>
            </tr>
          </thead>
          <tbody>
            {years.map((year) => (
              <tr key={year.year}>
                <td>{year.year}</td>
                <td className="num">{formatCents(year.beginningCents)}</td>
                <td className="num">{formatCents(year.distributionsCents)}</td>
                <td className="num">{formatCents(year.endingCents)}</td>
                <td className="num">{formatCents(year.suspendedLossesCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
